/**
 * What the tree screen computes from the tree, kept free of React so each rule
 * is tested on its own: search, the "Sans correspondance" badge, the code a new
 * node gets, and where a node may move.
 */

import type { CategoryNode, FamilyMapping, WebCategory } from "../../api";

/** The deepest level the backend accepts (`MAX_CATEGORY_DEPTH`). */
export const MAX_DEPTH = 4;

/** Every node of a branch, the head included. */
export function branchOf(node: CategoryNode): CategoryNode[] {
  return [node, ...node.children.flatMap(branchOf)];
}

/** The tree, flattened in display order, with each node's depth from zero. */
export function flatten(nodes: CategoryNode[], depth = 0): { node: CategoryNode; depth: number }[] {
  return nodes.flatMap((node) => [{ node, depth }, ...flatten(node.children, depth + 1)]);
}

/** Case and accents ignored: « eponge » finds « Éponge ». */
export function fold(text: string): string {
  return text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
}

/**
 * The tree narrowed to what matches, with the ancestors that lead to it.
 *
 * A match keeps its whole branch: searching « Tôlerie » shows the sub-family
 * and its segments, which is what somebody looking for it wants to see next.
 */
export function filterTree(nodes: CategoryNode[], query: string): CategoryNode[] {
  const needle = fold(query);
  if (!needle) return nodes;
  return nodes.flatMap((node) => {
    if (fold(node.category.label).includes(needle)) return [node];
    const children = filterTree(node.children, query);
    return children.length > 0 ? [{ ...node, children }] : [];
  });
}

/** Families mapped directly onto each category, by category id. */
export function familiesByCategory(mappings: FamilyMapping[]): Map<string, FamilyMapping[]> {
  const byCategory = new Map<string, FamilyMapping[]>();
  for (const row of mappings) {
    if (!row.category_id) continue;
    byCategory.set(row.category_id, [...(byCategory.get(row.category_id) ?? []), row]);
  }
  return byCategory;
}

/**
 * The nodes that carry the « Sans correspondance » badge.
 *
 * A branch no family is mapped into can never hold an article. The badge goes
 * on the **highest** node of such a branch only — its segments say nothing the
 * badge on their sub-family has not already said, and 40 identical badges down
 * a list hide the one that matters.
 */
export function unmappedHeads(
  nodes: CategoryNode[],
  families: Map<string, FamilyMapping[]>,
): Set<string> {
  const heads = new Set<string>();
  const hasMapping = (node: CategoryNode): boolean =>
    branchOf(node).some((item) => (families.get(item.category.id)?.length ?? 0) > 0);
  const visit = (node: CategoryNode) => {
    if (!hasMapping(node)) {
      heads.add(node.category.id);
      return;
    }
    node.children.forEach(visit);
  };
  nodes.forEach(visit);
  return heads;
}

/** `Peinture & Finition` → `peinture-finition`, as the backend derives slugs. */
export function slugOf(label: string): string {
  return fold(label)
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * The code a node created here gets: its path, upper-cased, as the import
 * gives one — `CARROSSERIE-MECANIQUE-CARROSSERIE-TOLERIE`. Nobody types a code
 * on this screen; a family maps onto the node itself, not onto its code.
 *
 * Capped at sixty characters like the column, keeping a short digest of the
 * whole so two long paths sharing their head still get two codes.
 */
export function codeFor(parentPath: string, label: string): string {
  const path = parentPath ? `${parentPath}/${slugOf(label)}` : slugOf(label);
  const code = path.replace(/\//g, "-").toUpperCase();
  if (code.length <= 60) return code;
  let hash = 0x811c9dc5;
  for (const char of path) {
    hash ^= char.charCodeAt(0);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return `${code.slice(0, 51)}-${hash.toString(16).toUpperCase().padStart(8, "0")}`;
}

/** How deep a branch goes beneath its head, the head counting as one. */
export function heightOf(node: CategoryNode): number {
  return 1 + Math.max(0, ...node.children.map(heightOf));
}

/**
 * Where a node may move: not into itself or its own branch, and not where its
 * branch would end up deeper than the tree allows. The server refuses both
 * too; offering only what it accepts spares a refusal after the click.
 */
export function moveTargets(
  roots: CategoryNode[],
  moving: CategoryNode,
): { category: WebCategory; depth: number }[] {
  const own = new Set(branchOf(moving).map((item) => item.category.id));
  const height = heightOf(moving);
  return flatten(roots)
    .filter(({ node }) => !own.has(node.category.id))
    .filter(({ node }) => node.category.depth + height <= MAX_DEPTH)
    .map(({ node, depth }) => ({ category: node.category, depth }));
}

/** Find a node by id anywhere in the tree. */
export function findNode(nodes: CategoryNode[], id: string): CategoryNode | undefined {
  for (const node of nodes) {
    if (node.category.id === id) return node;
    const found = findNode(node.children, id);
    if (found) return found;
  }
  return undefined;
}

/** The labels from the root down to the node, the node included. */
export function trail(nodes: CategoryNode[], id: string): string[] {
  for (const node of nodes) {
    if (node.category.id === id) return [node.category.label];
    const below = trail(node.children, id);
    if (below.length > 0) return [node.category.label, ...below];
  }
  return [];
}

/** The nodes from the root down to the one asked for, that node included. */
function chainTo(nodes: CategoryNode[], id: string): CategoryNode[] {
  for (const node of nodes) {
    if (node.category.id === id) return [node];
    const below = chainTo(node.children, id);
    if (below.length > 0) return [node, ...below];
  }
  return [];
}

/**
 * The attribute group a node gets from above: the deepest ancestor naming one,
 * and its trail. `null` when no ancestor does. The same rule the server applies
 * to an article (`resolve_attribute_group`), read on the tree.
 */
export function inheritedGroup(
  roots: CategoryNode[],
  id: string,
): { groupId: string; from: string } | null {
  const above = chainTo(roots, id).slice(0, -1);
  for (let index = above.length - 1; index >= 0; index -= 1) {
    const groupId = above[index]?.category.attribute_group_id;
    if (groupId) {
      return {
        groupId,
        from: above
          .slice(0, index + 1)
          .map((n) => n.category.label)
          .join(" › "),
      };
    }
  }
  return null;
}
